import React from 'react';
import { motion } from 'framer-motion';
import Footer from '../../../components/Footer';
import {
    Briefcase,
    GraduationCap,
    Stars,
    Clock,
    Bookmark,
    ArrowUpRight,
    Users,
    BookOpen,
    Award,
    Globe,
    Rocket,
    BookType,
    Target,
    Flag,
    Utensils,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Title from '../../../components/TitleComponent';

const bbaCourses = [
    { name: "BBA in Human Resource Management", path: "bachelor-of-business-administration" },
    { name: "BBA in Marketing Management", path: "bachelor-of-business-administration" },
    { name: "BBA in Finance Management", path: "bachelor-of-business-administration" },
    { name: "BBA in Digital Marketing", path: "bachelor-of-business-administration" },
    { name: "BBA in Aviation Management", path: "bachelor-of-business-administration" },
    { name: "BBA in Retail Management", path: "bachelor-of-business-administration" }
];

const bcomCourses = [
    { name: "B.Com (Hons) in Finance", path: "bachelor-of-commerce" },
    { name: "B.Com (Hons) in Taxation", path: "bachelor-of-commerce" },
    { name: "B.Com (Hons) in Accounting", path: "bachelor-of-commerce" }
];

const mbaCourses = [
    { name: "MBA in Human Resource Management", path: "master-of-business-administration" },
    { name: "MBA in Marketing Management", path: "master-of-business-administration" },
    { name: "MBA in Finance Management", path: "master-of-business-administration" },
    { name: "MBA in Digital Marketing", path: "master-of-business-administration" },
    { name: "MBA in Retail Management", path: "master-of-business-administration" },
    { name: "MBA in Hospital Administration", path: "master-of-business-administration" },
    { name: "MBA in Pharma Management", path: "master-of-business-administration" },
    { name: "MBA in Tourism Management", path: "master-of-business-administration" },
    { name: "MBA in Agri Business", path: "master-of-business-administration" },
    { name: "MBA in International Business", path: "master-of-business-administration" },
    { name: "MBA in Disaster Management", path: "master-of-business-administration" }
];

const aviationCourses = [
    { name: "Diploma in Aviation Management (DAM)", path: "best-diploma-in-aviation-management-college-in-dehradun-uttarakhand" },
    { name: "Bachelor in Aviation Management", path: "bachelor/aviation-management" },
    { name: "Master in Aviation Management", path: "masters/aviation-management" }
];


const Stats = () => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-4 md:my-8 px-4"
    >
        {[
            { icon: <Users className="w-6 h-6 md:w-8 md:h-8" />, number: "2000+", label: "Students Enrolled", color: "bg-blue-50" },
            { icon: <BookOpen className="w-6 h-6 md:w-8 md:h-8" />, number: "20+", label: "Programs Offered", color: "bg-green-50" },
            { icon: <Award className="w-6 h-6 md:w-8 md:h-8" />, number: "92%", label: "Placement Rate", color: "bg-purple-50" },
            { icon: <Globe className="w-6 h-6 md:w-8 md:h-8" />, number: "75+", label: "Industry Partners", color: "bg-orange-50" }
        ].map((stat, index) => (
            <motion.div
                key={index}
                className={`${stat.color} p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300`}
                whileHover={{ scale: 1.02 }}
            >
                <div className="text-[#0e4d38]">{stat.icon}</div>
                <h3 className="text-xl md:text-2xl font-bold mt-2 text-gray-800">{stat.number}</h3>
                <p className="text-gray-600 text-xs md:text-sm">{stat.label}</p>
            </motion.div>
        ))}
    </motion.div>
);

const CourseCard = ({ course, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="group w-full"
    >
        <Link to={course.path}>
            <div className="bg-white p-3 md:p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col justify-between gap-2 md:gap-3">
                <div>
                    <div className="flex justify-between items-start">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <BookType className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                        </div>
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="p-1 md:p-2 rounded-full hover:bg-gray-100"
                        >
                            <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4 text-gray-400 group-hover:text-[#0e4d38]" />
                        </motion.div>
                    </div>
                    <h3 className="text-sm md:text-lg font-semibold mt-2 md:mt-3 text-gray-800 group-hover:text-[#0e4d38] transition-colors line-clamp-2">
                        {course.name}
                    </h3>
                </div>
                <div className="flex items-center gap-2 md:gap-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{course.name.includes('MBA') ? '2 Years' : '3 Years'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Bookmark className="w-3 h-3" />
                        <span>{course.name.includes('MBA') ? 'PG' : 'UG'}</span>
                    </div>
                </div>
            </div>
        </Link>
    </motion.div>
);

const Department = ({ title, icon, courses }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-4 md:p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 border-b pb-4">
            <div className="p-2 md:p-3 bg-[#0e4d38] rounded-lg text-white">
                {icon}
            </div>
            <h4 className="text-lg md:text-xl font-semibold text-[#0e4d38]">{title}</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {courses.map((course, index) => (
                <CourseCard key={index} course={course} index={index} />
            ))}
        </div>
    </motion.div>
);

const FeatureList = ({ title, items, icon }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white p-4 md:p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 border-b pb-4">
            <div className="p-2 md:p-3 bg-[#0e4d38] rounded-xl text-white">
                {icon}
            </div>
            <h4 className="text-lg md:text-xl font-semibold text-[#0e4d38]">{title}</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {items.map((item, index) => (
                <motion.div
                    key={index}
                    className="p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                    whileHover={{ scale: 1.02 }}
                >
                    <div className="flex items-start gap-2">
                        <div className="w-2 h-2 mt-1.5 bg-[#23376c] rounded-full flex-shrink-0" />
                        <span className="text-gray-700 text-xs md:text-sm">{item}</span>
                    </div>
                </motion.div>
            ))}
        </div>
    </motion.div>
);

const HeroSection = () => (
    <div className="relative overflow-hidden bg-[#0e4d38] text-white py-8 md:py-16 px-4 md:px-6 rounded-xl mb-6 md:mb-8 mt-16 md:mt-20">
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl"
        >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                School of Management and Commerce
            </h1>
            <p className="text-sm md:text-lg text-gray-200 mb-4 md:mb-6 max-w-3xl">
                Empowering future business leaders with comprehensive management education and practical skills.
                Join us in shaping the future of business and commerce.
            </p>
            <Link to="https://admission.maya.edu.in/">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-[#0e4d38] px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-semibold flex items-center gap-2 group"
                >
                    Apply Now
                    <Rocket className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>
            </Link>
        </motion.div>
    </div>
);


const VisionMissionSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-6 rounded-xl shadow-lg"
        >
            <div className="flex items-center gap-3 mb-4 border-b pb-4">
                <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                    <Target className="w-6 h-6" />
                </div>
                <h4 className="text-xl font-semibold text-[#0e4d38]">Vision of School of Management & Commerce (SoMC)</h4>
            </div>
            <ul className="space-y-3 text-gray-700">
                {/* <li className="flex items-start gap-2">
                    <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                    <span>“Empowering Future Leaders”

                    </span>
                </li> */}
                <li className="flex items-start gap-2">
                    <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                    <span>"To be a leading center of excellence in management and commerce education, fostering innovative leaders, ethical professionals, and socially responsible entrepreneurs equipped to navigate and transform the dynamic global business landscape."</span>
                </li>
            </ul>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-6 rounded-xl shadow-lg"
        >
            <div className="flex items-center gap-3 mb-4 border-b pb-4">
                <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                    <Flag className="w-6 h-6" />
                </div>
                <h4 className="text-xl font-semibold text-[#0e4d38]">Mission Statements of SoMC</h4>
            </div>
            <ul className="space-y-3 text-gray-700">
                <h4>M1: Empowering Future Leaders</h4>
                <li className="flex items-start gap-2">
                    <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />

                    <span>To deliver a transformative education that builds essential skills, knowledge, and ethical values, preparing students to excel in the global business environment.</span>
                </li>
            </ul>
            <ul className="space-y-3 text-gray-700 mt-2">
                <h4>M2: Fostering Innovation and Practical Learning</h4>
                <li className="flex items-start gap-2">

                    <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />

                    <span>To create an enriching learning environment that emphasizes critical thinking, innovation, and real-world experiences through strong industry collaborations.</span>
                </li>
            </ul>
            <ul className="space-y-3 text-gray-700 mt-2">
                <h4>M3: Promoting Research and Social Responsibility</h4>
                <li className="flex items-start gap-2">

                    <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />

                    <span>To advance thought leadership and research in management and commerce, fostering sustainable and socially responsible business practices. </span>
                </li>
            </ul>
        </motion.div>
    </div>
);


const DeansMessage = () => (
    <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-3 mb-4 border-b pb-4">
            <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                <Flag className="w-6 h-6" />
            </div>
            <h4 className="text-xl font-semibold text-[#0e4d38]">Message from the Dean, School of Management & Commerce, Maya Devi University </h4>
        </div>
        <div className="space-y-4 text-gray-700">
            <p>
                Welcome to the School of Management & Commerce at Maya Devi University! It is a great privilege to lead an institution that is committed to fostering academic excellence and developing future leaders who are ready to tackle the challenges of the business world.
            </p>

            <p>At the School of Management & Commerce, we offer a dynamic learning environment that combines theoretical knowledge with practical insights. Our programs, including MBA, BBA, B.Com and M.Com, are designed to equip students with the critical thinking, leadership, and technical skills needed to excel in today's competitive global market. We emphasize innovation, research, and real-world applications to ensure that our graduates are prepared to lead organizations, drive business strategies, and contribute meaningfully to society.</p>

            <p>Our faculty comprises experienced professionals and academic experts who guide and mentor students, encouraging them to think creatively and challenge the status quo. Through industry partnerships, internships, workshops, and seminars, we ensure that our students stay ahead of the curve and develop skills that are essential for success in their careers.
            </p>
            <p>The School of Management & Commerce takes pride in its holistic approach to education, where we focus on the personal growth of each student, enhancing their leadership qualities, communication skills, and ethical standards. Our vision is to produce graduates who are not only competent professionals but also responsible global citizens.
            </p>
            <p>I invite you to explore our programs and discover how Maya Devi University can help you unlock your potential and shape your future. Together, we can create a learning experience that prepares you for success in the world of management and commerce.
            </p>
            <p>
                Warm regards,<br />
                Dr. Parikshit Kala<br />
                Dean, School of Management & Commerce<br />
                Maya Devi University

            </p>
        </div>

        {/* <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>The Faculty of Engineering offers a range of programs that incorporate cutting-edge techniques, providing students with a comprehensive engineering education. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>We prioritize systematic evaluation and feedback to support continuous learning and improvement. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>To inspire and motivate students, the faculty creates various platforms where they can cultivate their creativity and innovation. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>Additionally, our strategic international partnerships offer students valuable opportunities for global exposure and participation  in collaborative research projects with leading institutions.  </span>
            </li>
        </ul> */}
    </motion.div>
);



const Hod = () => (
    <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white p-6 rounded-xl shadow-lg"
    >
        <div className="flex items-center gap-3 mb-4 border-b pb-4">
            <div className="p-3 bg-[#0e4d38] rounded-lg text-white">
                <Flag className="w-6 h-6" />
            </div>
            <h4 className="text-xl font-semibold text-[#0e4d38]">Message from the Head of the Department, School of Management & Commerce, Maya Devi University</h4>
        </div>
        <div className="space-y-4 text-gray-700">
            <p>
                Welcome to the School of Management & Commerce at Maya Devi University! As the Head of the Department, I am honored to be part of an institution that is dedicated to shaping the leaders of tomorrow in the fields of management, commerce, and business.
            </p>

            <p>Our department offers a wide range of academic programs, including MBA, BBA, B.Com andM.Com, all designed to provide students with a strong foundation in business principles, analytical skills, and leadership qualities. We pride ourselves on providing an environment where students can learn, grow, and apply their knowledge to real-world challenges.</p>

            <p>At Maya Devi University, we focus on imparting both academic knowledge and practical skills. Our students benefit from a curriculum that blends theory with hands-on experience, engaging them with case studies, industry projects, internships, and exposure to contemporary business practices. Our faculty members, who bring a wealth of experience and expertise, are committed to mentoring students and helping them achieve their academic and career goals.
            </p>
            <p>Our vision is to nurture well-rounded individuals who are not only skilled in business practices but also understand the social, ethical, and global dimensions of the corporate world. We encourage students to think critically, work collaboratively, and adopt an entrepreneurial mindset that will serve them well in their careers.
            </p>
            <p>As you explore our programs, I encourage you to take full advantage of the opportunities and resources available to you. Whether you are pursuing a career in management, commerce, or entrepreneurship, our department is committed to providing the education and support you need to succeed.
            </p>
            <p>Thank you for considering the School of Management & Commerce at Maya Devi University. We look forward to helping you embark on your educational journey and supporting you every step of the way.
            </p>
            <p>
                Warm regards,<br />
                Dr. Ajay Chaurasia<br />
                Head of the Department, School of Management & Commerce<br />
                Maya Devi University

            </p>
        </div>

        {/* <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>The Faculty of Engineering offers a range of programs that incorporate cutting-edge techniques, providing students with a comprehensive engineering education. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>We prioritize systematic evaluation and feedback to support continuous learning and improvement. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>To inspire and motivate students, the faculty creates various platforms where they can cultivate their creativity and innovation. </span>
            </li>
            <li className="flex items-start gap-2">
                <div className="w-2 h-2 mt-2 bg-[#23376c] rounded-full flex-shrink-0" />
                <span>Additionally, our strategic international partnerships offer students valuable opportunities for global exposure and participation  in collaborative research projects with leading institutions.  </span>
            </li>
        </ul> */}
    </motion.div>
);


const Commerce = () => {
    return (
        <div className="min-h-screen bg-gray-50 font-poppins">
            <Title canonicalPath="/programmes/ug-courses/management-and-commerce" />
            <Title title="Best Management and Commerce University in Dehradun, Uttarakhand" description=" Maya Devi University is one of the best Management and Commerce University in Dehradun, offering world-class engineering programs in different Branches." />
            <div className="max-w-7xl mx-auto pt-16 md:pt-20 pb-8 md:pb-16 px-4 space-y-6 md:space-y-8">
                <HeroSection />
                <Stats />
                <VisionMissionSection />
                <DeansMessage />
                <Hod />

                <div className="space-y-6 md:space-y-8">
                    <Department
                        title="Bachelor of Business Administration (BBA)"
                        icon={<GraduationCap className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={bbaCourses}
                    />
                    <Department
                        title="Bachelor of Commerce (B.Com)"
                        icon={<BookType className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={bcomCourses}
                    />
                    {/* <Department
                        title="Master of Business Administration (MBA)"
                        icon={<Briefcase className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={mbaCourses}
                    />
                    <Department
                        title="Aviation Programs"
                        icon={<Utensils className="w-5 h-5 md:w-6 md:h-6" />}
                        courses={aviationCourses}
                    /> */}
                </div>

                <div className="space-y-6 md:space-y-8">
                    <FeatureList
                        title="USPs"
                        icon={<Stars className="w-6 h-6 text-[#0e4d38]" />}
                        items={[
                            "Industry-Integrated Curriculum",
                            "Expert Faculty with Industry Experience",
                            "International Business Exposure",
                            "Live Project Training",
                            "Business Incubation Center",
                            "Industry Mentorship Program",
                            "Career Development Workshops",
                            "Research Opportunities",
                            "Global Exchange Programs",
                            "Leadership Development Programs",
                            "Corporate Interface Sessions",
                            "Entrepreneurship Cell"
                        ]}
                    />

                    <FeatureList
                        title="Career Options"
                        icon={<Briefcase className="w-6 h-6 text-[#0e4d38]" />}
                        items={[
                            "Business Analyst",
                            "Marketing Manager",
                            "Financial Consultant",
                            "HR Manager",
                            "Investment Banker",
                            "Management Consultant",
                            "Digital Marketing Specialist",
                            "Business Development Manager",
                            "Corporate Strategist",
                            "Entrepreneur",
                            "Operations Manager",
                            "Project Manager"
                        ]}
                    />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Commerce;